import React from "react";
import { PageBuilderCruiseSliderFragment } from "types/graphql-types";
import PageBuilderSectionTitle from "../PageBuilderSectionTitle";
import { graphql } from "gatsby";
import { BackgroundColor } from "@silversea-ssc/ui-components-react";
import { configToFYCQuery } from "./utils";
import { cardColorForBackground } from "../pageBuilderRenderHelper";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import CruiseSliderBase from "components/CruisesSlider/CruiseSliderBase";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { useClientSideFeatureFlag } from "src/utils/feature-flags";

export type PageBuilderCruiseSliderProps = PageBuilderCruiseSliderFragment & {
  backgroundColor?: BackgroundColor;
};

const PageBuilderCruiseSlider: React.FC<PageBuilderCruiseSliderProps> = ({
  heading,
  footerNote,
  cruisePromosContent,
  shipsContent,
  backgroundColor = "white",
  ...props
}) => {
  const geolocation = useGeolocation();
  const language = useLanguage();
  const isCruiseTagsEnabled = useClientSideFeatureFlag("cruiseTags");
  const fycProps = isCruiseTagsEnabled
    ? props
    : { ...props, filterByCruiseTagCategories: undefined };
  const query = configToFYCQuery(fycProps, geolocation.countryCode, language);
  const cardBackgroundColor = cardColorForBackground(backgroundColor);

  return (
    <React.Fragment>
      <PageBuilderSectionTitle {...heading} />

      <CruiseSliderBase
        searchQuery={query}
        variantColor={cardBackgroundColor}
        showDestination
        promosContent={cruisePromosContent}
        shipsContent={shipsContent}
      />

      {footerNote?.localized && <div>{footerNote.localized}</div>}
    </React.Fragment>
  );
};

export default React.memo(PageBuilderCruiseSlider);

export const fragments = graphql`
  fragment PageBuilderCruiseSlider on SanityCruiseSlider {
    _key
    sectionLayoutConfig {
      ...SectionLayoutConfig
    }
    heading {
      ...SectionTitle
      ...SectionTitleCTAs
    }

    footerNote {
      localized
    }

    cruisePromosContent {
      ...FYCPromosContent
    }
    shipsContent {
      ...FYCShipsContent
    }

    filterByShip {
      name
    }

    filterByDestination {
      destinationWebCode
    }

    filterByOffer {
      ... on SanityExclusiveOfferPerPromo {
        _id
        configuration {
          code
          algoliaCode
        }
      }
    }

    filterByCruiseType
    filterByComboType

    filterByCruise: cruises {
      ... on SanityCruise {
        data {
          cruiseCode
        }
      }

      ... on SanitySpecialVoyage {
        data {
          cruiseCode
        }
      }
    }

    filterByCruiseCodes

    filterByCountries {
      ports {
        data {
          code
        }
      }
    }

    filterByPorts {
      data {
        code
      }
    }

    filterByFeatures
    filterByCruiseTagCategories {
      ...FYCCruisesTagCategoryContent
    }
    filterByCruiseGroups
    filterByFareCodes

    filterByCruisesLaunch {
      cruiseCodes
    }

    filterByLoadStress
  }
`;
